
import React, { useMemo, useState } from 'react';
import { Booklet, Lead } from '../types';
import { Eye, Users, Clock, Share2, TrendingUp, Smartphone, Monitor, Tablet, Mail, Activity, Calendar, ChevronDown, Check, CheckCircle, XCircle, FileText, Download, BarChart2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface AnalyticsProps {
  booklets?: Booklet[];
}

type TimeRange = '24h' | '7d' | '30d' | '90d' | 'year' | 'all';

const TIME_RANGES: { label: string; value: TimeRange }[] = [
    { label: 'Last 24 Hours', value: '24h' },
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
    { label: 'Past Year', value: 'year' },
    { label: 'All Time', value: 'all' },
];

// --- FIXED STATS CARD FOR MOBILE ---
const StatsCard: React.FC<{ title: string; value: string; icon: React.ReactNode; trend: string }> = ({ title, value, icon, trend }) => (
    <div className="bg-white p-6 pb-8 rounded-xl border border-warm/20 shadow-sm hover:shadow-md transition-all group min-w-0 w-full relative overflow-hidden flex flex-row md:flex-col items-center md:items-start justify-between gap-4 h-auto md:h-44">
        {/* Container for Icon: Auto width on mobile to prevent crushing text */}
        <div className="flex items-center md:items-start justify-between w-auto md:w-full md:mb-0 shrink-0">
            <div className="p-3 bg-warm/10 rounded-full text-dark group-hover:bg-dark group-hover:text-white transition-colors">
                {icon}
            </div>
            {/* Mobile Trend Badge */}
            <span className="md:hidden ml-3 text-[10px] font-bold text-green-700 bg-green-50 px-2 py-1 rounded-full border border-green-100 flex items-center gap-1">
                <TrendingUp size={10} /> {trend}
            </span>
        </div>
        
        {/* Container for Text: Takes remaining space */}
        <div className="min-w-0 relative z-10 flex-1 md:w-full text-right md:text-left">
            {/* Desktop Trend Badge */}
            <div className="hidden md:flex justify-end absolute top-0 right-0 -mt-12">
                 <span className="text-[10px] font-bold text-green-700 bg-green-50 px-2 py-1 rounded-full border border-green-100 flex items-center gap-1">
                    <TrendingUp size={10} /> {trend}
                </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-serif text-dark font-medium truncate mb-1 md:mb-2 mt-0 md:mt-2">{value}</h3>
            <p className="text-[10px] md:text-xs text-cool uppercase tracking-wide font-semibold opacity-70 truncate">{title}</p>
        </div>
    </div>
);

const DeviceBar: React.FC<{ icon: React.ReactNode, label: string, value: number, total: number }> = ({ icon, label, value, total }) => {
    const safeTotal = Number(total) || 1;
    const safeValue = Number(value) || 0;
    const percent = Math.round((safeValue / safeTotal) * 100) || 0;
    
    return (
        <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-warm/30 transition-colors w-full min-w-0 group">
            <div className="text-cool mb-2 group-hover:text-dark transition-colors">{icon}</div>
            <div className="text-2xl font-serif font-medium text-dark mb-1">{percent}%</div>
            <div className="text-[10px] uppercase text-cool font-bold tracking-widest mb-3">{label}</div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${percent}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full bg-dark"
                />
            </div>
        </div>
    );
};

const Analytics: React.FC<AnalyticsProps> = ({ booklets = [] }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const selectedLabel = TIME_RANGES.find(r => r.value === timeRange)?.label || 'Filter';

  const safeBooklets = useMemo(() => Array.isArray(booklets) ? booklets : [], [booklets]);

  // Aggregate Stats
  const totalViews = useMemo(() => 
    safeBooklets.reduce((acc, curr) => acc + (Number(curr.stats?.views) || 0), 0), 
  [safeBooklets]);

  const totalReaders = useMemo(() => 
    safeBooklets.reduce((acc, curr) => acc + (Number(curr.stats?.uniqueReaders) || 0), 0), 
  [safeBooklets]);

  const totalShares = useMemo(() => 
    safeBooklets.reduce((acc, curr) => acc + (Number(curr.stats?.shares) || 0), 0), 
  [safeBooklets]);
  
  // Average reading time is derived from real session counters
  // (totalTimeSeconds / timeSessions), falling back to the legacy
  // avgTimeSeconds field for anything recorded before tracking existed.
  const bookletAvgTime = (b: Booklet): number => {
    const total = Number(b.stats?.totalTimeSeconds) || 0;
    const sessions = Number(b.stats?.timeSessions) || 0;
    if (sessions > 0) return Math.round(total / sessions);
    return Number(b.stats?.avgTimeSeconds) || 0;
  };

  const avgTime = useMemo(() => {
    const totalSeconds = safeBooklets.reduce((acc, curr) => acc + (Number(curr.stats?.totalTimeSeconds) || 0), 0);
    const totalSessions = safeBooklets.reduce((acc, curr) => acc + (Number(curr.stats?.timeSessions) || 0), 0);
    if (totalSessions > 0) return Math.round(totalSeconds / totalSessions);
    // Legacy fallback (pre-tracking data)
    if (safeBooklets.length === 0) return 0;
    const legacy = safeBooklets.reduce((acc, curr) => acc + (Number(curr.stats?.avgTimeSeconds) || 0), 0);
    return Math.round(legacy / safeBooklets.length);
  }, [safeBooklets]);

  // Devices Breakdown
  const devices = useMemo(() => 
    safeBooklets.reduce((acc, curr) => {
        const d = curr.stats?.devices || { desktop: 0, mobile: 0, tablet: 0 };
        acc.desktop += Number(d.desktop) || 0;
        acc.mobile += Number(d.mobile) || 0;
        acc.tablet += Number(d.tablet) || 0;
        return acc;
    }, { desktop: 0, mobile: 0, tablet: 0 }),
  [safeBooklets]);

  const totalDeviceCount = (devices.desktop + devices.mobile + devices.tablet) || 1;

  // Sort Booklets by Views for the Performance Table
  const performanceList = useMemo(() => 
    [...safeBooklets].sort((a, b) => ((Number(b.stats?.views) || 0)) - ((Number(a.stats?.views) || 0))),
  [safeBooklets]);

  // --- FIXED LEAD GENERATION LOGIC ---
  const allLeads = useMemo(() => {
      if (!Array.isArray(safeBooklets)) return [];

      const leads: Lead[] = safeBooklets.flatMap(b => {
          let bookLeads: Lead[] = [];
          
          // 1. Process Modern Leads (collectedLeads array of objects)
          if (b.collectedLeads && Array.isArray(b.collectedLeads)) {
              const processed = b.collectedLeads.map(l => {
                  let leadDate = Date.now();
                  const val = l.date as any;
                  
                  // Handle Firestore Timestamp vs String vs Number
                  if (val) {
                      if (typeof val === 'object' && 'seconds' in val) {
                          leadDate = val.seconds * 1000;
                      } else if (typeof val === 'string') {
                          leadDate = new Date(val).getTime();
                      } else if (typeof val === 'number') {
                          leadDate = val;
                      }
                  }

                  return {
                      email: l.email,
                      marketing: l.marketing || false,
                      date: leadDate,
                      source: b.title
                  };
              });
              bookLeads = [...bookLeads, ...processed];
          }
          
          // 2. Process Legacy Emails (collectedEmails array of strings)
          if (b.collectedEmails && Array.isArray(b.collectedEmails)) {
              const existingEmails = new Set(bookLeads.map(l => l.email));
              
              b.collectedEmails.forEach(emailStr => {
                  if (!existingEmails.has(String(emailStr))) {
                      // Fallback to Booklet creation date
                      let bookletDate = Date.now();
                      const createdVal = b.createdAt as any;
                      if (createdVal) {
                          if (typeof createdVal === 'object' && 'seconds' in createdVal) {
                              bookletDate = createdVal.seconds * 1000;
                          } else {
                              bookletDate = new Date(createdVal).getTime();
                          }
                      }

                      bookLeads.push({
                          email: String(emailStr),
                          marketing: false,
                          date: bookletDate,
                          source: b.title
                      });
                  }
              });
          }
          return bookLeads;
      });
      
      leads.sort((a, b) => b.date - a.date);

      if (timeRange === 'all') return leads;

      const now = Date.now();
      const msPerDay = 24 * 60 * 60 * 1000;
      
      return leads.filter(item => {
          if (!item.date || isNaN(item.date)) return false;

          const diff = now - item.date;
          switch (timeRange) {
              case '24h': return diff < (1 * msPerDay);
              case '7d': return diff < (7 * msPerDay);
              case '30d': return diff < (30 * msPerDay);
              case '90d': return diff < (90 * msPerDay);
              case 'year': return diff < (365 * msPerDay);
              default: return true;
          }
      });
  }, [safeBooklets, timeRange]);

  const downloadCSV = () => {
      const headers = ['Email Address', 'Source Publication', 'Marketing Status', 'Date'];
      const rows = allLeads.map(lead => [
          lead.email,
          lead.source,
          lead.marketing ? 'Subscribed' : 'Not Subscribed',
          new Date(lead.date).toLocaleDateString()
      ]);

      const csvContent = "data:text/csv;charset=utf-8," 
          + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `esselle_leads_${timeRange}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const formatDuration = (seconds: number) => {
      if (!seconds) return '0s';
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}m ${s}s`;
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-10 space-y-8 md:space-y-12 overflow-x-hidden">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-20">
        <div>
            <h1 className="font-serif text-3xl md:text-4xl text-dark">Analytics</h1>
            <p className="text-cool mt-1 text-sm md:text-base">Real-time engagement intelligence.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
                <button 
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="flex items-center gap-2 bg-white border border-warm/20 text-dark px-4 py-2.5 rounded-full text-sm font-bold shadow-sm hover:bg-gray-50 transition-all min-w-[160px] justify-between w-full sm:w-auto"
                >
                    <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-warm" />
                        <span>{selectedLabel}</span>
                    </div>
                    <ChevronDown size={14} className={`transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                    {isFilterOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setIsFilterOpen(false)} />
                            <motion.div 
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-warm/10 py-2 z-20 overflow-hidden"
                            >
                                {TIME_RANGES.map((range) => (
                                    <button
                                        key={range.value}
                                        onClick={() => { setTimeRange(range.value); setIsFilterOpen(false); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${timeRange === range.value ? 'font-bold text-dark bg-gray-50' : 'text-cool'}`}
                                    >
                                        {range.label}
                                        {timeRange === range.value && <Check size={14} className="text-green-600" />}
                                    </button>
                                ))}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>

            <div className="hidden md:flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-green-700 bg-green-50 px-3 py-1.5 rounded-full self-start md:self-auto border border-green-100 shadow-sm h-[42px] flex-shrink-0">
                <Activity size={12} className="text-green-600 animate-pulse" />
                Live
            </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 w-full">
        <StatsCard title="Total Views" value={totalViews.toLocaleString()} icon={<Eye size={18} />} trend="+12%" />
        <StatsCard title="Readers" value={totalReaders.toLocaleString()} icon={<Users size={18} />} trend="+8%" />
        <StatsCard title="Avg. Time" value={`${Math.floor(avgTime / 60)}m ${avgTime % 60}s`} icon={<Clock size={18} />} trend="+5%" />
        <StatsCard title="Shares" value={totalShares.toLocaleString()} icon={<Share2 size={18} />} trend="+24%" />
      </div>

      {/* PUBLICATION PERFORMANCE SECTION */}
      <div className="w-full">
         <div className="mb-4">
             <h2 className="font-serif text-xl text-dark flex items-center gap-2">
                 <BarChart2 size={20} className="text-warm" />
                 Publication Performance
             </h2>
             <p className="text-xs text-cool mt-1">Detailed metrics per uploaded document.</p>
         </div>

         <div className="flex flex-col gap-4">
            {performanceList.length === 0 ? (
                <div className="p-12 text-center text-cool text-sm bg-white rounded-xl border border-warm/20">No publications to display.</div>
            ) : (
                performanceList.map((item) => {
                    const thumbUrl = item.coverUrl || (item.pages && item.pages.length > 0 ? item.pages[0] : null);

                    return (
                        <div key={item.id} className="bg-white rounded-xl border border-warm/20 p-4 md:p-6 shadow-sm hover:shadow-md transition-all w-full">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                                {/* Thumbnail & Info (Spans 5 cols on Large) */}
                                <div className="lg:col-span-5 flex items-center gap-5 min-w-0">
                                    <div className="w-20 md:w-24 bg-gray-100 rounded-sm border border-gray-200 overflow-hidden shrink-0 shadow-sm relative self-start aspect-[3/4]">
                                        {thumbUrl ? (
                                            <img src={thumbUrl} className="w-full h-full object-cover" alt={item.title} />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-warm bg-gray-50"><FileText size={24}/></div>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1 overflow-hidden">
                                        <h3 className="font-serif text-lg font-bold text-dark truncate mb-1" title={item.title}>{item.title}</h3>
                                        <div className="flex items-center gap-2 text-xs text-cool mb-2 font-mono opacity-80">
                                            <Calendar size={12} />
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <span className="bg-gray-100 text-cool text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">{item.visibility || 'Public'}</span>
                                            {item.status === 'draft' && <span className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">Draft</span>}
                                        </div>
                                    </div>
                                </div>

                                {/* Metrics Columns (Spans 7 cols on Large) */}
                                <div className="lg:col-span-7 w-full pt-4 lg:pt-0 border-t lg:border-t-0 border-warm/10">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                        <div className="text-center lg:text-left min-w-0">
                                            <p className="text-[10px] uppercase text-cool font-bold tracking-wider mb-1 truncate">Total Views</p>
                                            <p className="text-xl md:text-2xl font-serif text-dark font-medium truncate">{item.stats?.views?.toLocaleString() || 0}</p>
                                        </div>
                                        <div className="text-center lg:text-left min-w-0">
                                            <p className="text-[10px] uppercase text-cool font-bold tracking-wider mb-1 truncate">Readers</p>
                                            <p className="text-xl md:text-2xl font-serif text-dark font-medium truncate">{item.stats?.uniqueReaders?.toLocaleString() || 0}</p>
                                        </div>
                                        <div className="text-center lg:text-left min-w-0">
                                            <p className="text-[10px] uppercase text-cool font-bold tracking-wider mb-1 truncate">Avg. Time</p>
                                            <p className="text-xl md:text-2xl font-serif text-dark font-medium truncate">{formatDuration(bookletAvgTime(item))}</p>
                                        </div>
                                        <div className="text-center lg:text-left min-w-0">
                                            <p className="text-[10px] uppercase text-cool font-bold tracking-wider mb-1 truncate">Shares</p>
                                            <p className="text-xl md:text-2xl font-serif text-dark font-medium truncate">{item.stats?.shares?.toLocaleString() || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
         </div>
      </div>

      {/* Device Breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border border-warm/20 p-5 md:p-6">
          <h2 className="font-serif text-lg text-dark mb-4 flex items-center gap-2">
              <Smartphone size={18} className="text-warm" /> Audience Devices
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <DeviceBar icon={<Monitor size={20} />} label="Desktop" value={devices.desktop} total={totalDeviceCount} />
              <DeviceBar icon={<Smartphone size={20} />} label="Mobile" value={devices.mobile} total={totalDeviceCount} />
              <DeviceBar icon={<Tablet size={20} />} label="Tablet" value={devices.tablet} total={totalDeviceCount} />
          </div>
      </div>

      {/* LEAD GENERATION SECTION */}
      <div className="bg-white rounded-2xl shadow-sm border border-warm/20 overflow-hidden flex flex-col min-w-0 w-full">
        <div className="px-5 py-5 md:px-6 md:py-6 border-b border-warm/10 bg-gray-50/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
                <h2 className="font-serif text-lg md:text-xl text-dark flex items-center gap-2">
                    <Mail size={20} className="text-warm" />
                    Lead Generation
                </h2>
                <p className="text-xs text-cool mt-1">Emails collected via access gates ({selectedLabel})</p>
            </div>
            <div className="flex gap-2">
                {allLeads.length > 0 && (
                    <button 
                        onClick={downloadCSV}
                        className="text-xs font-bold bg-white hover:bg-gray-50 border border-gray-200 text-dark px-4 py-2 rounded-full shadow-sm flex items-center gap-2 transition-colors"
                    >
                        <Download size={14} /> CSV
                    </button>
                )}
                <span className="text-xs font-bold text-dark bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm">
                    {allLeads.length} Leads
                </span>
            </div>
        </div>
        
        <div className="w-full">
            {allLeads.length > 0 ? (
                <>
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[600px]">
                            <thead>
                                <tr className="bg-white text-xs font-bold text-cool uppercase tracking-wider border-b border-warm/10">
                                    <th className="px-6 py-4">Email Address</th>
                                    <th className="px-6 py-4">Source Publication</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-warm/10">
                                {allLeads.map((lead, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 text-dark font-medium flex items-center gap-2">
                                            <Mail size={14} className="text-warm" />
                                            {lead.email}
                                        </td>
                                        <td className="px-6 py-4 text-cool text-sm">{lead.source}</td>
                                        <td className="px-6 py-4">
                                            {lead.marketing ? (
                                                <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-1 rounded-full uppercase border border-green-200 flex items-center gap-1 w-fit">
                                                    <CheckCircle size={10} /> Subscribed
                                                </span>
                                            ) : (
                                                <span className="bg-gray-100 text-gray-500 text-[10px] font-bold px-2 py-1 rounded-full uppercase border border-gray-200 flex items-center gap-1 w-fit">
                                                    <XCircle size={10} /> Not Subscribed
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-cool text-sm text-right font-mono text-xs opacity-70">{new Date(lead.date).toLocaleDateString()}</td> 
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="md:hidden divide-y divide-warm/10">
                        {allLeads.map((lead, idx) => (
                            <div key={idx} className="p-4 flex flex-col gap-3">
                                <div className="flex justify-between items-start">
                                    <div className="font-medium text-dark flex items-center gap-2 break-all">
                                        <Mail size={14} className="text-warm shrink-0" />
                                        {lead.email}
                                    </div>
                                    <span className="text-[10px] text-cool opacity-60 shrink-0 font-mono">
                                        {new Date(lead.date).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-cool text-xs line-clamp-1 flex-1 mr-2">{lead.source}</span>
                                    {lead.marketing ? (
                                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-1 rounded-full uppercase border border-green-200 flex items-center gap-1 shrink-0">
                                            <CheckCircle size={10} /> Subscribed
                                        </span>
                                    ) : (
                                        <span className="bg-gray-100 text-gray-500 text-[10px] font-bold px-2 py-1 rounded-full uppercase border border-gray-200 flex items-center gap-1 shrink-0">
                                            <XCircle size={10} /> Not Subscribed
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="p-12 text-center flex flex-col items-center justify-center bg-gray-50/30">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Mail size={24} className="text-warm/50" />
                    </div>
                    <p className="text-cool text-sm font-medium">No leads collected in {selectedLabel.toLowerCase()}.</p>
                    <p className="text-xs text-cool/50 mt-1">Enable "Require Email" on your publications to start collecting leads.</p>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
